import {initConfig} from '@/config/config';
import APP_ENTRIES, {type AppEntry} from '@/consts/appEntries';
import REFETCH_DATA_TYPES from '@/consts/refetchDataTypes';
import {initContent} from '@/services/content/content';
import eventMessagesService from '@/services/events/eventMessagesService';
import appRouterService from '@/services/route/appRouterService';
import type {AppPadding} from '@/state/slices/appSlice';
import {setActiveModule, setInitialized, setLanguageCode, setMarketCode, setPadding} from '@/state/slices/appSlice';
import {setBadgesData, setRewardsData} from '@/state/slices/loyaltySlice';
import {dispatch} from '@/state/store';
import type {BadgesData, RewardsData} from '@/types/loyalty';

type AppInitPayload = {
    marketCode?: string;
    languageCode?: string;
    entry?: AppEntry;
    badgesConfiguration?: BadgesData;
    rewardsConfiguration?: RewardsData;
    paddingTop?: number;
    paddingBottom?: number;
    paddingBottomMenu?: number;
};

/**
 * TODO: Remove once AEM content is migrated from "v2" market folders to plain market codes.
 * The host app sends market codes like "gbv2" but AEM content now lives under "gb".
 * This strips the trailing "v2" suffix so the correct AEM persisted query path is resolved.
 */
const normalizeMarketCode = (code: string): string => {
    return code.replace(/v2$/i, '');
};

/**
 * Handles UWM_INIT_MARKET_LOCALE.
 * Expected payload: { marketCode, languageCode, entry, badgesConfiguration?, rewardsConfiguration? }
 */
export const proceedAppInit = async (payload: AppInitPayload | null | undefined) => {
    try {
        const {
            marketCode,
            languageCode,
            entry,
            badgesConfiguration,
            rewardsConfiguration,
            paddingTop,
            paddingBottom,
            paddingBottomMenu,
        } = payload ?? {};

        if (!marketCode || !languageCode) {
            eventMessagesService.sendModuleInitializationError(
                `Initialized data is not correct. MarketCode: ${marketCode} LanguageCode: ${languageCode}`
            );
            return;
        }

        const validEntries = Object.values(APP_ENTRIES) as string[];
        if (!entry || !validEntries.includes(entry)) {
            eventMessagesService.sendModuleInitializationError(
                `Initialized data is not correct: entry must be one of [${validEntries.join(', ')}], got: ${entry}`
            );
            return;
        }

        await initConfig(normalizeMarketCode(marketCode));
        await initContent(normalizeMarketCode(marketCode), languageCode);

        dispatch(setMarketCode(marketCode));
        dispatch(setLanguageCode(languageCode));
        dispatch(setActiveModule(entry));

        if (entry === APP_ENTRIES.BADGES) {
            if (!badgesConfiguration) {
                eventMessagesService.sendModuleInitializationError(
                    'Initialized data is not correct: badgesConfiguration is missing'
                );
                return;
            }
            dispatch(setBadgesData(badgesConfiguration));
            appRouterService.replaceToBadgesPage();
        } else {
            if (!rewardsConfiguration) {
                eventMessagesService.sendModuleInitializationError(
                    'Initialized data is not correct: rewardsConfiguration is missing'
                );
                return;
            }
            dispatch(setRewardsData(rewardsConfiguration));
            appRouterService.replaceToRewardsPage();
        }

        const padding: AppPadding = {
            ...(paddingTop != null && {top: paddingTop}),
            ...(paddingBottom != null && {bottom: paddingBottom}),
            ...(paddingBottomMenu != null && {bottomMenu: paddingBottomMenu}),
        };
        dispatch(setPadding(padding));

        dispatch(setInitialized(true));
        eventMessagesService.sendDataSuccessReceivedMessage();
    } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e);
        eventMessagesService.sendModuleInitializationError(message);
    }
};

/**
 * Handles UWM_REFRESH_DATA — used after the user returns from an
 * external link. Only the relevant data is updated; market/language/content and
 * the current route are intentionally left untouched.
 */
export const proceedDataRefresh = (
    payload:
        | {dataType?: string; badgesConfiguration?: BadgesData; rewardsConfiguration?: RewardsData}
        | null
        | undefined
) => {
    const {dataType, badgesConfiguration, rewardsConfiguration} = payload ?? {};

    if (dataType === REFETCH_DATA_TYPES.REWARDS) {
        if (!rewardsConfiguration) return;
        dispatch(setRewardsData(rewardsConfiguration));
    } else if (dataType === REFETCH_DATA_TYPES.BADGES) {
        if (!badgesConfiguration) return;
        dispatch(setBadgesData(badgesConfiguration));
    } else {
        return;
    }

    eventMessagesService.sendDataSuccessReceivedMessage();
};
